import React, { useState, useContext, useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import { Dialog } from "@reach/dialog"
import "@reach/dialog/styles.css"
import { SmartDateFormat } from "../../util/dates"
import { UserContext } from "../HomeApp"
import type { Band } from "../../types/bands"
import ConfirmActionViaEmailCode from "@components/ConfirmActionViaEmailCode"
import { OutlineButton } from "@components/shared/Button"
import { openAuthDialog } from "@components/Authentication"
import { ShareUrlButton } from "@components/ShareUrlButton"
import styles from "./styles.module.css"

interface BacklineProps {
  name: string
  backlineOfferings: string
  backlineOfferingsUpdatedAt?: string
  venueInfo?: Band["additional_info"]
  location?: Band["location"]
  isOpen: boolean
  onClose: () => void
}

const LAST_CONFIRMED_EMAIL_ALLOWED_HOURS = 24

export const Backline: React.FC<BacklineProps> = ({
  name,
  backlineOfferings,
  backlineOfferingsUpdatedAt,
  venueInfo,
  location,
  isOpen,
  onClose,
}) => {
  const { user } = useContext(UserContext)
  const dispatch = useDispatch()
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [isUserConfirmed, setIsUserConfirmed] = useState(false)

  const isLocationSecret = venueInfo?.is_location_secret

  // Check if user has verified their email within the past 24 hours
  const lastVerifiedEmailAt = user?.last_verified_email_at
  useEffect(() => {
    if (lastVerifiedEmailAt) {
      const verifiedAt = new Date(lastVerifiedEmailAt)
      const now = new Date()
      const twentyFourHoursAgo = new Date(
        now.getTime() - LAST_CONFIRMED_EMAIL_ALLOWED_HOURS * 60 * 60 * 1000,
      )

      if (verifiedAt >= twentyFourHoursAgo) {
        setIsUserConfirmed(true)
      }
    }
  }, [lastVerifiedEmailAt])

  const backlineShareUrl = useMemo(() => {
    if (typeof window === "undefined") return ""
    const url = new URL(window.location.href)
    url.searchParams.set("backline", "true")
    return url.toString()
  }, [])

  // Helper function to format enum values for display
  const formatValue = (value: string | undefined): string => {
    if (!value) return "Not specified"
    return value
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const formatAgeRequirements = (value: string | undefined): string => {
    if (!value) return "Not specified"
    return value === "all_ages" ? "All Ages" : value
  }

  const formatFoodOptions = (value: string | undefined): string => {
    if (!value) return "Not specified"
    const mapping: Record<string, string> = {
      full_menu: "Full Menu",
      snacks: "Snacks",
      no_food: "No Food",
    }
    return mapping[value] || formatValue(value)
  }

  const formatDrinkOptions = (value: string | undefined): string => {
    if (!value) return "Not specified"
    const mapping: Record<string, string> = {
      full_bar: "Full Bar",
      small_bar: "Small Bar",
      byo: "BYO",
      dry: "Dry",
    }
    return mapping[value] || formatValue(value)
  }

  const formatIndoorsOutdoors = (value: string | undefined): string => {
    if (!value) return "Not specified"
    const mapping: Record<string, string> = {
      indoor_only: "Indoor Only",
      outdoor_only: "Outdoor Only",
      both: "Indoor and Outdoor Spaces",
    }
    return mapping[value] || formatValue(value)
  }

  const formatPetFriendly = (value: string | undefined): string => {
    if (!value) return "Not specified"
    const mapping: Record<string, string> = {
      yes: "Yes",
      no: "No",
      service_animals_only: "Service Animals Only",
    }
    return mapping[value] || formatValue(value)
  }

  const renderLocation = () => {
    if (isLocationSecret === "tickets_only") {
      return (
        <div>
          Location is secret, and will be shared with you when you buy a ticket.
        </div>
      )
    }

    if (isLocationSecret === "after_validation" && !isUserConfirmed) {
      return (
        <div className="mb-4 text-sm">
          <p className="italic text-yellow-700">
            The location is secret, we need to confirm you are a human before we
            can share it with you.
          </p>
          {!showConfirmation &&
            (user ? (
              <OutlineButton
                className="text-gray-800 text-sm mt-2"
                onClick={() => setShowConfirmation(true)}
              >
                I Am Human
              </OutlineButton>
            ) : (
              <OutlineButton
                className="text-gray-800 text-sm mt-2"
                onClick={() => dispatch(openAuthDialog())}
              >
                Sign In / Sign Up
              </OutlineButton>
            ))}
          <ConfirmActionViaEmailCode
            actionText="Secret Location"
            isOpen={showConfirmation}
            setIsOpen={setShowConfirmation}
            setIsConfirmed={setIsUserConfirmed}
          />
        </div>
      )
    }

    return (
      <>
        {isUserConfirmed && (
          <p className="font-bold mb-1 text-green-700 text-sm">
            Thanks for being a human {user?.first_name} :) Here's the secret
            location...
          </p>
        )}
        <p className="text-sm">{location.displayName}</p>
        {venueInfo?.entrance_instructions && (
          <div className="my-4">
            <h3 className="font-bold text-gray-700 text-xs uppercase">
              Entrance Instructions
            </h3>
            <p className="text-sm whitespace-pre-wrap">
              {venueInfo.entrance_instructions}
            </p>
          </div>
        )}
      </>
    )
  }

  const hasVenueInfo =
    venueInfo &&
    (venueInfo.hours ||
      venueInfo.capacity ||
      venueInfo.age_requirements ||
      venueInfo.food_options ||
      venueInfo.drink_options ||
      venueInfo.indoors_outdoors ||
      venueInfo.pet_friendly ||
      venueInfo.entrance_instructions ||
      location)

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onDismiss={onClose}
        aria-label={`Venue details and backline offerings for ${name}`}
        style={{ boxShadow: "0px 0px 20px #90cdf4" }}
      >
        <div className="flex justify-end">
          <button
            className="close-button focus:outline-none text-lg font-bold text-gray-600 hover:text-gray-800"
            onClick={onClose}
            aria-label="Close venue details dialog"
          >
            ×
          </button>
        </div>

        <div className="grid grid-cols-12">
          <div className="col-span-12">
            <div className="flex items-center gap-2">
              <h1
                className="font-extrabold text-xl text-blue-600"
                style={{ textShadow: "0px 0px 5px rgb(140 231 254)" }}
              >
                {name}
              </h1>
              {backlineShareUrl ? (
                <ShareUrlButton
                  nadaUrl={backlineShareUrl}
                  renderMode="icon"
                  className="text-blue-600"
                />
              ) : null}
            </div>
            <div className="font-bold text-gray-800 text-sm text-uppercase uppercase">
              Venue Details
            </div>
          </div>
        </div>

        {/* Venue Information Section - Listed First */}
        {hasVenueInfo && (
          <div className="grid grid-cols-12 mt-4 mb-6">
            <div className="col-span-12">
              <div className="mb-4">
                <h3 className="font-bold text-gray-700 text-xs uppercase">
                  Location
                </h3>
                {renderLocation()}
              </div>

              {venueInfo?.hours && (
                <div className="mb-4">
                  <h3 className="font-bold text-gray-700 text-xs uppercase">
                    Hours
                  </h3>
                  <p className="text-sm">{venueInfo.hours}</p>
                </div>
              )}

              {venueInfo?.capacity && (
                <div className="mb-4">
                  <h3 className="font-bold text-gray-700 text-xs uppercase">
                    Capacity
                  </h3>
                  <p className="text-sm">{venueInfo.capacity}</p>
                </div>
              )}

              {venueInfo?.age_requirements && (
                <div className="mb-4">
                  <h3 className="font-bold text-gray-700 text-xs uppercase">
                    Age Requirements
                  </h3>
                  <p className="text-sm">
                    {formatAgeRequirements(venueInfo.age_requirements)}
                  </p>
                </div>
              )}

              {venueInfo?.food_options && (
                <div className="mb-4">
                  <h3 className="font-bold text-gray-700 text-xs uppercase">
                    Food Options
                  </h3>
                  <p className="text-sm">
                    {formatFoodOptions(venueInfo.food_options)}
                  </p>
                </div>
              )}

              {venueInfo?.drink_options && (
                <div className="mb-4">
                  <h3 className="font-bold text-gray-700 text-xs uppercase">
                    Drink Options
                  </h3>
                  <p className="text-sm">
                    {formatDrinkOptions(venueInfo.drink_options)}
                  </p>
                </div>
              )}

              {venueInfo?.indoors_outdoors && (
                <div className="mb-4">
                  <h3 className="font-bold text-gray-700 text-xs uppercase">
                    Indoor/Outdoor Spaces
                  </h3>
                  <p className="text-sm">
                    {formatIndoorsOutdoors(venueInfo.indoors_outdoors)}
                  </p>
                </div>
              )}

              {venueInfo?.pet_friendly && (
                <div className="mb-4">
                  <h3 className="font-bold text-gray-700 text-xs uppercase">
                    Pet Friendly
                  </h3>
                  <p className="text-sm">
                    {formatPetFriendly(venueInfo.pet_friendly)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Backline Section */}
        <div className="grid grid-cols-12">
          <div className="col-span-12">
            <h2 className="font-bold text-gray-800 text-sm text-uppercase uppercase">
              Backline
            </h2>
            {backlineOfferingsUpdatedAt && (
              <div className="text-xs text-gray-500 mb-2">
                Updated:{" "}
                {SmartDateFormat({
                  dateStr: backlineOfferingsUpdatedAt,
                  includeTime: true,
                })}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-12 mt-2">
          <div className="col-span-12">
            {backlineOfferings ? (
              <div className="bg">
                <div
                  className={styles.backlineMarkdown}
                  dangerouslySetInnerHTML={{ __html: backlineOfferings }}
                />
              </div>
            ) : (
              <div className="text-gray-600 italic">
                No backline information available.
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </>
  )
}

export default Backline
